"use client";
import React, {
  ChangeEvent,
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useLocale, useTranslations } from "next-intl";

import { EnumLocation, LocationEnumType } from "@/enums/locationEnum";
import { routing, useRouter } from "@/i18n/routing";
import IconArrow from "@/icons/ui/navigation/arrow.svg";
import { useLocationStore } from "@/store/location.store";
import { Filter } from "@/types/SearchFilter.types";
import {
  Cities,
  Districts,
  Name,
  RegionData,
  Streets,
} from "@/types/store/location.types";
import { CloseButton } from "@/ui/CloseButton";
import Button from "@/ui/Common/Button";
import { cn } from "@/utils/cn";
import { createLogger } from "@/utils/logger";

import SearchLocationInput from "./SearchLocationInput"; // Import the new component
import styles from "./style.module.scss";

type GetNameType = (name: Name, defaultName?: boolean) => string;

type SearchTypeProps = {
  selectedFilters: Filter;
  setSelectedFilters: React.Dispatch<React.SetStateAction<Filter>>;
  setSearchTitle: React.Dispatch<React.SetStateAction<string>>;
  dealType: string;
  style?: CSSProperties;
  handleSearch: (filters?: Filter) => void;
  filteredRegionValue: RegionData[];
  searchTitleValue: string; // New prop
  onClosePopup: () => void; // New prop
  onSearchTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void; // New prop
};

const logger = createLogger("search-location-modal");

const SearchLocation = ({
  selectedFilters,
  setSelectedFilters,
  dealType,
  style,
  handleSearch,
  setSearchTitle,
  filteredRegionValue,
  searchTitleValue, // New prop
  onClosePopup, // New prop
  onSearchTitleChange, // New prop
}: SearchTypeProps) => {
  const t = useTranslations();
  const locale = useLocale();
  const defaultLocale = routing.defaultLocale;
  const router = useRouter();
  const { recommended, regionValue } = useLocationStore();

  const modalRef = useRef<HTMLDivElement>(null);
  const inputLocationRef = useRef<HTMLInputElement>(null);

  const getName = useMemo<GetNameType>(
    () =>
      (name, defaultName = true) => {
        if (defaultName) {
          return name[defaultLocale] || name[locale];
        }
        return name[locale] || name[defaultLocale];
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale],
  );

  const {
    cityToRegionMap,
    regionMap,
    regionMapWithDistrict,
    regionMapWithMicrodistrict,
    regionMapWithStreet,
    regionMapWithMetroStation,
    cityMap,
    cityMapWithMultidistrict,
    cityMapWithStreet,
    districtStreetMap,
    districtMicrodistrictMap,
  } = useMemo(() => {
    const cityToRegionMap = new Map<string, string>();
    const regionMap = new Map<string, Cities[]>();
    const regionMapWithDistrict = new Map<string, Streets[]>();
    const regionMapWithMicrodistrict = new Map<string, Streets[]>();
    const regionMapWithStreet = new Map<string, Streets[]>();
    const regionMapWithMetroStation = new Map<string, any[]>();
    const cityMap = new Map<string, Districts[]>();
    const cityMapWithMultidistrict = new Map<string, Streets[]>();
    const cityMapWithStreet = new Map<string, Streets[]>();
    const districtStreetMap = new Map<string, Streets[]>();
    const districtMicrodistrictMap = new Map<string, Streets[]>();

    regionValue.forEach((region) => {
      regionMap.set(getName(region.name), region.cities || []);
      regionMapWithMetroStation.set(
        getName(region.name),
        region.metroStations || [],
      );
      (region.cities || []).forEach((city) => {
        regionMapWithDistrict.set(getName(region.name), city.districts || []);
        cityMap.set(getName(city.name), city.districts || []);
        cityToRegionMap.set(getName(city.name), getName(region.name));
        (city.districts || []).forEach((district) => {
          regionMapWithMicrodistrict.set(
            getName(region.name),
            district.microdistricts || [],
          );
          regionMapWithStreet.set(getName(region.name), district.streets || []);
          cityMapWithMultidistrict.set(
            getName(city.name),
            district.microdistricts || [],
          );
          cityMapWithStreet.set(getName(city.name), district.streets || []);
          districtStreetMap.set(getName(district.name), district.streets || []);
          districtMicrodistrictMap.set(
            getName(district.name),
            district.microdistricts || [],
          );
        });
      });
    });

    return {
      cityToRegionMap,
      regionMap,
      regionMapWithDistrict,
      regionMapWithMicrodistrict,
      regionMapWithStreet,
      regionMapWithMetroStation,
      cityMap,
      cityMapWithMultidistrict,
      cityMapWithStreet,
      districtStreetMap,
      districtMicrodistrictMap,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSelection = (
    e: ChangeEvent<HTMLInputElement>,
    list: string[],
    item: string,
    type: LocationEnumType,
  ) => {
    const { checked } = e.target;

    setSelectedFilters((prev) => {
      let updateLocation;

      switch (type) {
        case EnumLocation.region:
          const cityInRegion = prev[EnumLocation.city]?.filter((city) => {
            const checkCity = regionMap
              .get(item)
              ?.map((el) => getName(el.name));

            return !checkCity?.includes(city);
          });
          const districtsInRegion = prev[EnumLocation.district]?.filter(
            (district) => {
              const checkDistrict = regionMapWithDistrict
                .get(item)
                ?.map((el) => getName(el.name));
              return !checkDistrict?.includes(district);
            },
          );
          const microDistrictsInRegion = prev[
            EnumLocation.microdistrict
          ]?.filter((microdistrict) => {
            const checkMicroDistrict = regionMapWithMicrodistrict
              .get(item)
              ?.map((el) => getName(el.name));
            return !checkMicroDistrict?.includes(microdistrict);
          });
          const StreetsInRegion = prev[EnumLocation.street]?.filter(
            (street) => {
              const checkDistrict = regionMapWithStreet
                .get(item)
                ?.map((el) => getName(el.name));
              return !checkDistrict?.includes(street);
            },
          );
          const metroStationsInRegion = prev[EnumLocation.metroStation]?.filter(
            (metroStation) => {
              const checkMetroStation = regionMapWithMetroStation
                .get(item)
                ?.map((el) => getName(el.name));
              return !checkMetroStation?.includes(metroStation);
            },
          );

          updateLocation = checked
            ? [...new Set([...(prev[type] ?? []), item])]
            : ((prev[EnumLocation.city] = cityInRegion),
              (prev[EnumLocation.district] = districtsInRegion),
              (prev[EnumLocation.microdistrict] = microDistrictsInRegion),
              (prev[EnumLocation.street] = StreetsInRegion),
              (prev[EnumLocation.metroStation] = metroStationsInRegion),
              prev[type]?.filter((el) => el !== item) ?? []);
          if (checked) {
            return {
              ...prev,
              [type]: [...new Set([...(prev[type] ?? []), item])],
              [EnumLocation.city]: [],
              [EnumLocation.district]: [],
              [EnumLocation.microdistrict]: [],
              [EnumLocation.street]: [],
              [EnumLocation.metroStation]: [],
            };
          } else {
            const updatedRegions =
              prev[type]?.filter((el) => el !== item) ?? [];
            const citiesToKeep = prev[EnumLocation.city]?.filter((city) => {
              const parentRegion = cityToRegionMap.get(city);
              return parentRegion && updatedRegions.includes(parentRegion);
            });
            const districtsToKeep = prev[EnumLocation.district]?.filter(
              (district) => {
                let keep = false;
                cityMap.forEach((districtsInCity, cityName) => {
                  if (
                    citiesToKeep?.includes(cityName) &&
                    districtsInCity.some((d) => getName(d.name) === district)
                  ) {
                    keep = true;
                  }
                });
                return keep;
              },
            );
            return {
              ...prev,
              [type]: updatedRegions,
              [EnumLocation.city]: citiesToKeep,
              [EnumLocation.district]: districtsToKeep,
              [EnumLocation.microdistrict]: [],
              [EnumLocation.street]: [],
              [EnumLocation.metroStation]: [],
            };
          }

        case EnumLocation.city:
          const districtInCity = prev[EnumLocation.district]?.filter(
            (district) => {
              const checkDistrict = cityMap
                .get(item)
                ?.map((el) => getName(el.name));
              return !checkDistrict?.includes(district);
            },
          );
          const multidistrictInDistrictInCity = prev[
            EnumLocation.microdistrict
          ]?.filter((microdistrict) => {
            const checkMultiDistrict = cityMapWithMultidistrict
              .get(item)
              ?.map((el) => getName(el.name));
            return !checkMultiDistrict?.includes(microdistrict);
          });
          const streetsInDistrictInCity = prev[EnumLocation.street]?.filter(
            (street) => {
              const checkStreetDistrict = cityMapWithStreet
                .get(item)
                ?.map((el) => getName(el.name));
              return !checkStreetDistrict?.includes(street);
            },
          );

          updateLocation = checked
            ? [...new Set([...(prev[type] ?? []), item])]
            : ((prev[EnumLocation.district] = districtInCity),
              (prev[EnumLocation.microdistrict] =
                multidistrictInDistrictInCity),
              (prev[EnumLocation.street] = streetsInDistrictInCity),
              prev[type]?.filter((el) => el !== item) ?? []);
          break;

        case EnumLocation.district:
          const multidistrictInDistrict = prev[
            EnumLocation.microdistrict
          ]?.filter((microdistrict) => {
            const checkMicrodistrict = districtMicrodistrictMap
              .get(item)
              ?.map((el) => getName(el.name));
            return !checkMicrodistrict?.includes(microdistrict);
          });
          const streetInDistrict = prev[EnumLocation.street]?.filter(
            (district) => {
              const checkDistrict = districtStreetMap
                .get(item)
                ?.map((el) => getName(el.name));
              return !checkDistrict?.includes(district);
            },
          );

          updateLocation = checked
            ? [...new Set([...(prev[type] ?? []), item])]
            : ((prev[EnumLocation.microdistrict] = multidistrictInDistrict),
              (prev[EnumLocation.street] = streetInDistrict),
              prev[type]?.filter((el) => el !== item) ?? []);
          break;

        default:
          break;
      }

      return {
        ...prev,
        // [type]: updateLocation,
        [type]: checked
          ? [...new Set([...(prev[type] ?? []), item])]
          : (prev[type]?.filter((el) => el !== item) ?? []),
      };
    });
  };

  const availableCities = useMemo(
    () =>
      selectedFilters.region?.flatMap((region) => {
        return regionMap.get(region) || [];
      }),
    [selectedFilters.region, regionMap],
  );
  // logger.info("🚀 ~ availableCities:", availableCities);

  const availableDistricts = useMemo(
    () => selectedFilters.city?.flatMap((city) => cityMap.get(city) || []),
    [selectedFilters.city, cityMap],
  );

  const availableStreets = useMemo(
    () =>
      selectedFilters.district?.flatMap(
        (district) => districtStreetMap.get(district) || [],
      ),
    [selectedFilters.district, districtStreetMap],
  );

  const availableMicrodistricts = useMemo(
    () =>
      selectedFilters.district?.flatMap(
        (district) => districtMicrodistrictMap.get(district) || [],
      ),
    [selectedFilters.district, districtMicrodistrictMap],
  );

  const availableMetroStations = useMemo(
    () =>
      selectedFilters.region?.flatMap(
        (region) => regionMapWithMetroStation.get(region) || [],
      ),
    [selectedFilters.region, regionMapWithMetroStation],
  );

  const closeWindow = () => {
    onClosePopup();
  };

  useEffect(() => {
    inputLocationRef.current?.focus();
  }, []);

  const handleClickNext = () => {
    handleSearch();
    closeWindow(); // Replaced state update
  };

  const handleClickClear = () => {
    const cleared = {
      ...selectedFilters,
      region: [],
      city: [],
      district: [],
      microdistrict: [],
      street: [],
      metroStation: [],
    };
    setSearchTitle("");
    setSelectedFilters(cleared);
    handleSearch(cleared);
    closeWindow();
  };

  const handleCitySearch = useCallback(
    (nameOfRegionOrCityFromEffect: string) => {
      // nameOfRegionOrCityFromEffect is expected to be the value from selectedFilters.region[0]
      // It's used to set both the city filter and to find its corresponding region via cityToRegionMap.

      const actualRegion = cityToRegionMap.get(nameOfRegionOrCityFromEffect);

      if (!actualRegion) {
        logger.warn(
          `No region found in cityToRegionMap for: ${nameOfRegionOrCityFromEffect}`,
        );
        return;
      }

      setSelectedFilters((prevFilters) => {
        const newRegionArray = [actualRegion];
        const newCityArray = [nameOfRegionOrCityFromEffect];
        const newDistrictArray: string[] = [];
        const newMicrodistrictArray: string[] = [];
        const newStreetArray: string[] = [];

        // Check if the relevant parts of the filter would actually change
        const regionChanged =
          JSON.stringify(prevFilters.region) !== JSON.stringify(newRegionArray);
        const cityChanged =
          JSON.stringify(prevFilters.city) !== JSON.stringify(newCityArray);
        const districtNeedClearing =
          prevFilters.district && prevFilters.district.length > 0;
        const microdistrictNeedClearing =
          prevFilters.microdistrict && prevFilters.microdistrict.length > 0;
        const streetNeedClearing =
          prevFilters.street && prevFilters.street.length > 0;

        if (
          !regionChanged &&
          !cityChanged &&
          !districtNeedClearing &&
          !microdistrictNeedClearing &&
          !streetNeedClearing
        ) {
          // If none of the parts that this function intends to modify are changing,
          // return the previous state to prevent a re-render cycle.
          return prevFilters;
        }
        logger.info(
          `Updating filters in handleCitySearch. Old: ${JSON.stringify({
            region: prevFilters.region,
            city: prevFilters.city,
            district: prevFilters.district,
          })}, New: ${JSON.stringify({ region: newRegionArray, city: newCityArray, district: newDistrictArray })}`,
        );
        return {
          ...prevFilters,
          region: newRegionArray,
          city: newCityArray,
          district: newDistrictArray,
          microdistrict: newMicrodistrictArray,
          street: newStreetArray,
          metroStation: [],
        };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cityToRegionMap, setSelectedFilters], // logger removed - it's stable and doesn't affect re-renders
  );

  const showRegions = useMemo(
    () => filteredRegionValue || regionValue,
    [filteredRegionValue, regionValue],
  );

  // State for collapsible sections
  const [openSections, setOpenSections] = useState({
    region: true,
    city: true,
    district: true,
    microdistrict: true,
    street: true,
    metroStation: true,
  });

  // Handler for toggling section open/close
  const handleSectionToggle = (section: keyof typeof openSections) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleApply = () => {
    logger.info("Applying location filter:", selectedFilters);

    // Create a deep copy of selectedFilters to avoid modifying the original state directly
    const filtersForApi = JSON.parse(JSON.stringify(selectedFilters));

    // Helper to find the English name and convert to lower case
    const getEnglishLowerCase = (
      itemName: string,
      collection: any[] | undefined,
      itemType: string,
    ) => {
      if (!collection) {
        return itemName.toLowerCase();
      }
      const itemObject = collection.find(
        (item) => getName(item.name) === itemName,
      );
      if (
        itemObject &&
        itemObject.name &&
        typeof itemObject.name.en === "string"
      ) {
        return itemObject.name.en;
      }
      logger.warn(
        `Could not find English name for ${itemType}: ${itemName}, sending as is (lowercase).`,
      );
      return itemName.toLowerCase(); // Fallback to the original name in lowercase
    };

    if (filtersForApi.region && Array.isArray(filtersForApi.region)) {
      filtersForApi.region = filtersForApi.region.map((regionName: string) =>
        getEnglishLowerCase(regionName, regionValue, "region"),
      );
    }

    if (filtersForApi.city && Array.isArray(filtersForApi.city)) {
      filtersForApi.city = filtersForApi.city.map((cityName: string) =>
        getEnglishLowerCase(cityName, availableCities, "city"),
      );
    }

    if (filtersForApi.district && Array.isArray(filtersForApi.district)) {
      filtersForApi.district = filtersForApi.district.map(
        (districtName: string) =>
          getEnglishLowerCase(districtName, availableDistricts, "district"),
      );
    }

    if (
      filtersForApi.microdistrict &&
      Array.isArray(filtersForApi.microdistrict)
    ) {
      filtersForApi.microdistrict = filtersForApi.microdistrict.map(
        (microdistrictName: string) =>
          getEnglishLowerCase(
            microdistrictName,
            availableMicrodistricts,
            "microdistrict",
          ),
      );
    }

    if (filtersForApi.street && Array.isArray(filtersForApi.street)) {
      filtersForApi.street = filtersForApi.street.map((streetName: string) =>
        getEnglishLowerCase(streetName, availableStreets, "street"),
      );
    }

    if (
      filtersForApi.metroStation &&
      Array.isArray(filtersForApi.metroStation)
    ) {
      filtersForApi.metroStation = filtersForApi.metroStation.map(
        (metroStationName: string) =>
          getEnglishLowerCase(
            metroStationName,
            availableMetroStations,
            "metroStation",
          ),
      );
    }

    logger.info(
      "Filters being sent to API (raw filtersForApi object):",
      filtersForApi,
    );
    logger.info(
      "Filters being sent to API (JSON.stringify for detailed inspection):",
      JSON.stringify(filtersForApi, null, 2),
    );
    handleSearch(filtersForApi);
    closeWindow();
  };

  const handleRegionSelect = (regionId: string | null) => {
    logger.info("Region selected:", regionId);
    const region = regionValue.find((r) => r.name[defaultLocale] === regionId);
    setSelectedFilters((prev) => ({
      ...prev,
      region: region ? [getName(region.name)] : [],
      city: [],
      district: [],
      microdistrict: [],
      street: [],
      metroStation: [],
    }));
    if (regionId && region) {
      handleCitySearch(getName(region.name));
    }
  };

  const handleCitySelect = (cityId: string | null) => {
    logger.info("City selected:", cityId);
    const city = availableCities?.find((c) => c.name[defaultLocale] === cityId);
    setSelectedFilters((prev) => ({
      ...prev,
      city: city ? [getName(city.name)] : [],
      district: [],
      microdistrict: [],
      street: [],
      metroStation: [],
    }));
  };

  return (
    <>
      <div
        style={style}
        className={cn(styles.categoryList)}
      >
        <div className={cn("grow w-full overflow-y-auto md:overflow-hidden")}>
          <div className={"block md:hidden"}>
            <h3 className="!border-none">
              <span>{t("Location selection")}</span>

              <CloseButton
                size={25}
                className={"p-2"}
                onClick={closeWindow}
              />
            </h3>
          </div>
          <div
            className={cn(styles.modalSearchInputContainer, "block md:hidden")}
          >
            <SearchLocationInput
              inputRef={inputLocationRef}
              value={searchTitleValue}
              onChange={onSearchTitleChange}
              placeholder={t("Enter city, region, district")}
            />
          </div>
          <div className={"w-full max-w-full md:overflow-x-auto"}>
            <div className={cn(styles.locationMainWrapper)}>
              {/* Regions */}
              <h3
                className={
                  "md:border-r select-none md:cursor-default cursor-pointer"
                }
                style={{}}
                onClick={() => handleSectionToggle("region")}
              >
                {t("Select region")}
                <IconArrow
                  className={cn(
                    "inline-block transition-transform md:hidden ml-2 text-red",
                    openSections.region ? "rotate-[90deg]" : "rotate-[270deg]",
                  )}
                />
              </h3>
              {openSections.region && (
                <div className={cn(styles.locationWrapper, "p-[2rem]")}>
                  {showRegions.map((region) => (
                    <label
                      key={region.name[defaultLocale]}
                      className={styles.nestedSubcategory}
                    >
                      <input
                        className={cn(styles.checkbox, styles.inputChecked)}
                        type="checkbox"
                        checked={selectedFilters.region?.includes(
                          getName(region?.name),
                        )}
                        onChange={(e) =>
                          toggleSelection(
                            e,
                            selectedFilters.region!,
                            getName(region.name),
                            EnumLocation.region,
                          )
                        }
                      />
                      <span className={styles.locationTitle}>
                        {getName(region.name, false)}
                      </span>
                    </label>
                  ))}
                </div>
              )}
              {/* Cities */}

              <h3
                className={
                  "md:border-r select-none md:cursor-default cursor-pointer"
                }
                style={{}}
                onClick={() => handleSectionToggle("city")}
              >
                {t("Select city")}
                <IconArrow
                  className={cn(
                    "inline-block transition-transform md:hidden ml-2 text-red",
                    openSections.city ? "rotate-[90deg]" : "rotate-[270deg]",
                  )}
                />
              </h3>

              <div className={styles.locationWrapper}>
                {openSections.city &&
                  selectedFilters &&
                  selectedFilters.region &&
                  selectedFilters.region.length > 0 &&
                  availableCities &&
                  availableCities.length > 0 &&
                  selectedFilters.region.map((regionName: string) => {
                    const region = regionValue.find((r) => {
                      return getName(r.name) === regionName;
                    });

                    if (!region?.cities?.length) {
                      return;
                    }

                    return (
                      <div
                        className={"space-y-[0.5rem] p-[2rem] "}
                        key={regionName}
                      >
                        {selectedFilters?.region?.length && (
                          <h4>{getName(region.name, false)}</h4>
                        )}
                        {region?.cities.map((city) => {
                          return (
                            <label
                              key={city.name[defaultLocale]}
                              className={styles.nestedSubcategory}
                            >
                              <input
                                className={cn(
                                  styles.checkbox,
                                  styles.inputCheckedSub,
                                )}
                                type="checkbox"
                                checked={selectedFilters.city?.includes(
                                  getName(city.name),
                                )}
                                onChange={(e) =>
                                  toggleSelection(
                                    e,
                                    selectedFilters.city!,
                                    getName(city.name),
                                    EnumLocation.city,
                                  )
                                }
                              />
                              <span className={styles.locationSubTitle}>
                                {getName(city.name, false)}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    );
                  })}
              </div>
              {/* District */}
              <h3
                className={
                  "md:border-r select-none md:cursor-default cursor-pointer"
                }
                style={{}}
                onClick={() => handleSectionToggle("district")}
              >
                {t("Select district")}
                <IconArrow
                  className={cn(
                    "inline-block transition-transform md:hidden ml-2 text-red",
                    openSections.district
                      ? "rotate-[90deg]"
                      : "rotate-[270deg]",
                  )}
                />
              </h3>
              <div className={styles.locationWrapper}>
                {openSections.district &&
                  selectedFilters &&
                  selectedFilters.region &&
                  selectedFilters.city &&
                  selectedFilters.region.length > 0 &&
                  selectedFilters.city.length > 0 &&
                  availableDistricts &&
                  availableDistricts.length > 0 &&
                  selectedFilters.city.map((cityName: string) => {
                    const city = availableCities?.find((c) => {
                      return getName(c.name) === cityName;
                    });

                    if (!city?.districts?.length) {
                      return;
                    }

                    return (
                      <div
                        className={"space-y-[0.5rem] p-[2rem] "}
                        key={cityName}
                      >
                        {selectedFilters?.city?.length && (
                          <h4>{getName(city.name, false)}</h4>
                        )}
                        {city?.districts.map((district) => {
                          return (
                            <label
                              key={district.name[defaultLocale]}
                              className={styles.nestedSubcategory}
                            >
                              <input
                                className={cn(
                                  styles.checkbox,
                                  styles.inputCheckedSub,
                                )}
                                type="checkbox"
                                checked={selectedFilters.district?.includes(
                                  getName(district.name),
                                )}
                                onChange={(e) =>
                                  toggleSelection(
                                    e,
                                    selectedFilters.district!,
                                    getName(district.name),
                                    EnumLocation.district,
                                  )
                                }
                              />
                              <span className={styles.locationSubTitle}>
                                {getName(district.name, false)}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    );
                  })}
              </div>
              {/* Microdistrict */}
              <h3
                className={
                  "md:border-r select-none md:cursor-default cursor-pointer"
                }
                style={{}}
                onClick={() => handleSectionToggle("microdistrict")}
              >
                {t("Select microdistrict")}
                <IconArrow
                  className={cn(
                    "inline-block transition-transform md:hidden ml-2 text-red",
                    openSections.microdistrict
                      ? "rotate-[90deg]"
                      : "rotate-[270deg]",
                  )}
                />
              </h3>
              <div className={styles.locationWrapper}>
                {openSections.microdistrict &&
                  selectedFilters &&
                  selectedFilters.region &&
                  selectedFilters.city &&
                  selectedFilters.district &&
                  selectedFilters.region.length > 0 &&
                  selectedFilters.city.length > 0 &&
                  selectedFilters.district.length > 0 &&
                  availableMicrodistricts &&
                  availableMicrodistricts.length > 0 &&
                  selectedFilters.district.map((districtName: string) => {
                    const district = availableDistricts?.find((d) => {
                      return getName(d.name) === districtName;
                    });

                    if (!district?.microdistricts?.length) {
                      return;
                    }

                    return (
                      <div
                        className={"space-y-[0.5rem] p-[2rem]"}
                        key={districtName}
                      >
                        {selectedFilters?.district?.length && (
                          <h4>{getName(district.name, false)}</h4>
                        )}
                        {district.microdistricts.map((microdistrict) => {
                          return (
                            <label
                              key={microdistrict.name[defaultLocale]}
                              className={styles.nestedSubcategory}
                            >
                              <input
                                className={cn(
                                  styles.checkbox,
                                  styles.inputCheckedSub,
                                )}
                                type="checkbox"
                                checked={selectedFilters.microdistrict?.includes(
                                  getName(microdistrict.name),
                                )}
                                onChange={(e) =>
                                  toggleSelection(
                                    e,
                                    selectedFilters.microdistrict!,
                                    getName(microdistrict.name),
                                    EnumLocation.microdistrict,
                                  )
                                }
                              />
                              <span className={styles.locationSubTitle}>
                                {getName(microdistrict.name, false)}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    );
                  })}
              </div>
              {/* Street */}
              <h3
                className={"select-none md:cursor-default cursor-pointer"}
                style={{}}
                onClick={() => handleSectionToggle("street")}
              >
                {t("Select street")}
                <IconArrow
                  className={cn(
                    "inline-block transition-transform md:hidden ml-2 text-red",
                    openSections.street ? "rotate-[90deg]" : "rotate-[270deg]",
                  )}
                />
              </h3>
              <div className={styles.locationWrapper}>
                {openSections.street &&
                  selectedFilters &&
                  selectedFilters.region &&
                  selectedFilters.city &&
                  selectedFilters.district &&
                  selectedFilters.microdistrict &&
                  selectedFilters.region.length > 0 &&
                  selectedFilters.city.length > 0 &&
                  selectedFilters.district.length > 0 &&
                  availableStreets &&
                  availableStreets.length > 0 &&
                  selectedFilters.district.map((districtName: string) => {
                    const district = availableDistricts?.find((s) => {
                      return getName(s.name) === districtName;
                    });

                    if (!district?.streets?.length) {
                      return;
                    }

                    return (
                      <div
                        className={"space-y-[0.5rem] p-[2rem]"}
                        key={districtName}
                      >
                        {selectedFilters?.district?.length && (
                          <h4>{getName(district.name, false)}</h4>
                        )}
                        {district.streets.map((street) => {
                          return (
                            <label
                              key={street.name[defaultLocale]}
                              className={styles.nestedSubcategory}
                            >
                              <input
                                className={cn(
                                  styles.checkbox,
                                  styles.inputCheckedSub,
                                )}
                                type="checkbox"
                                checked={selectedFilters.street?.includes(
                                  getName(street.name),
                                )}
                                onChange={(e) =>
                                  toggleSelection(
                                    e,
                                    selectedFilters.street!,
                                    getName(street.name),
                                    EnumLocation.street,
                                  )
                                }
                              />
                              <span className={styles.locationSubTitle}>
                                {getName(street.name, false)}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    );
                  })}
              </div>
              {/* Metro Stations */}
              <h3
                className={"select-none md:cursor-default cursor-pointer"}
                style={{}}
                onClick={() => handleSectionToggle("metroStation")}
              >
                {t("Metro Station")}
                <IconArrow
                  className={cn(
                    "inline-block transition-transform md:hidden ml-2 text-red",
                    openSections.metroStation
                      ? "rotate-[90deg]"
                      : "rotate-[270deg]",
                  )}
                />
              </h3>
              <div className={styles.locationWrapper}>
                {openSections.metroStation &&
                  selectedFilters &&
                  selectedFilters.region &&
                  selectedFilters.region.length > 0 &&
                  availableMetroStations &&
                  availableMetroStations.length > 0 &&
                  selectedFilters.region.map((regionName: string) => {
                    const region = regionValue.find((r) => {
                      return getName(r.name) === regionName;
                    });

                    if (!region?.metroStations?.length) {
                      return;
                    }

                    return (
                      <div
                        className={"space-y-[0.5rem] p-[2rem]"}
                        key={regionName}
                      >
                        {selectedFilters?.region?.length && (
                          <h4>{getName(region.name, false)}</h4>
                        )}
                        {region.metroStations.map((metroStation) => {
                          return (
                            <label
                              key={metroStation.name[defaultLocale]}
                              className={styles.nestedSubcategory}
                            >
                              <input
                                className={cn(
                                  styles.checkbox,
                                  styles.inputCheckedSub,
                                )}
                                type="checkbox"
                                checked={selectedFilters.metroStation?.includes(
                                  getName(metroStation.name),
                                )}
                                onChange={(e) =>
                                  toggleSelection(
                                    e,
                                    selectedFilters.metroStation!,
                                    getName(metroStation.name),
                                    EnumLocation.metroStation,
                                  )
                                }
                              />
                              <span className={styles.locationSubTitle}>
                                {getName(metroStation.name, false)}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
        <div className={cn(styles.nextButtonWrapper)}>
          <Button
            type="white"
            className="max-w-[15.5rem] h-[5.4rem]"
            onClick={handleClickClear}
          >
            {t("Clear")}
          </Button>
          <Button
            type="red"
            className="max-w-[15.5rem] h-[5.4rem]"
            onClick={handleApply}
          >
            {t("Apply")}
          </Button>
        </div>
      </div>
      <div
        className={styles.categoryList_B}
        onClick={closeWindow}
      ></div>
    </>
  );
};
export default SearchLocation;
